import axios from 'axios';
import { supabase } from '../supabase';
import { appApiRuntime, imagePublicRuntime, buildImagePublicUrl } from '@/config/featureFlags';
import { runAssetUploadService } from '@/core/analysis/resultExecution/runAssetUploadService';

const API_BASE_URL = appApiRuntime.baseURL;

// 全局默认超时（axios 默认 0 = 永不超时）。移动端弱网下 TCP 静默断连时，
// 无超时的请求会永远 pending，造成 pending 页无限转圈且后端 runs 永不提交（漏单）。
// 单独设置 timeout 的调用方（如 image-proxy 15s、creditService）不受影响。
axios.defaults.timeout = 30000;

// /photos/ 复用同一批次授权端点（SEC-01 §5.3）：将保存角色组装为批次 descriptor。
// - original 保存：仅 original descriptor（source = uploadSource || imageData）。
// - processed 保存：必须同时携带 original + processed descriptor；original source 来自调用方传入的
//   originalSource（fileObject || imageData），processed source 为当前 imageData 参数（processed 字节）。
//   即使 Backend 判断 original 对象也缺失，服务仍会在同一批次收敛、处理所有 object_upload_required=true 的 item。
// 注意：descriptor.fileType 可为 MIME（image/png）或短枚举（png），服务内部归一化；返回的 fileType 为后端标准值。
const buildPhotosUploadDescriptors = ({
    hashId,
    imageType,
    fileType,
    imageData,
    uploadSource = null,
    originalHashId = null,
    originalSource = null,
    originalFileType = null
}) => {
    const original = imageType === 'processed'
        ? {
            hashId: originalHashId,
            fileType: originalFileType,
            source: originalSource
        }
        : {
            hashId,
            fileType,
            source: uploadSource || imageData
        };

    const descriptors = { original };
    if (imageType === 'processed') {
        descriptors.processed = {
            hashId,
            fileType,
            source: imageData
        };
    }
    return descriptors;
};

// api服务类
const apiService = {
    // 查询图片信息的方法
    async queryImageInfo(hashId, imageType, originalHashId = null) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const params = {
                image_type: imageType,
                ...(imageType === 'processed' && originalHashId ? { original_hash_id: originalHashId } : {})
            };

            const response = await axios({
                method: 'GET',
                url: `${API_BASE_URL}/photos/${hashId}/`,
                params,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`
                },
                validateStatus: function (status) {
                    return status === 200 || status === 410;
                }
            });

            if (response.status === 200) {
                // 从响应中获取对应类型的图片信息
                const imageInfo = imageType === 'processed'
                    ? response.data.processed_image
                    : response.data.original_image;

                return {
                    exists: true,
                    data: response.data,
                    imageUrl: imageInfo?.image_url
                };
            } else if (response.status === 410) {
                // 图片逻辑删除，但判定为存在
                const detail = response.data.detail;
                const imageInfo = detail;
                console.log('[API] 图片逻辑删除，但判定为存在:', detail);

                return {
                    exists: true,
                    isDeleted: true,
                    data: detail,
                    imageUrl: imageInfo?.img_url
                };
            }
        } catch (error) {
            if (error.response?.status === 404) {
                return { exists: false };
            }
            throw error;
        }
    },

    // 保存图片到服务器
    async saveImageToServer(hashId, imageData, fileType, imageType, options = {}) {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const saveOptions = typeof options === 'string'
                ? { originalHashId: options }
                : (options || {});
            const {
                originalHashId = null,
                uploadSource = null,
                // 以下仅用于 processed 授权（内部参数，非 /photos/ wire contract）：
                originalSource = null,
                originalFileType = null
            } = saveOptions;

            // 首先检查图片是否已存在
            const queryResult = await this.queryImageInfo(hashId, imageType, originalHashId);
            if (queryResult.exists) {
                if (queryResult.isDeleted) {
                    // 对于已删除的图片，跳过 R2 上传，但请求 POST 接口，使用查询的 detail 作为数据
                    // 从 img_url 提取 file_type，如果不存在
                    const imgUrl = queryResult.data.img_url;
                    const fileType = queryResult.data.file_type || (imgUrl ? imgUrl.split('.').pop() : 'png'); // 默认 png

                    // 构建请求数据
                    const requestData = {
                        hash_id: queryResult.data.hash_id,
                        img_url: imgUrl,
                        file_type: fileType,
                        image_type: queryResult.data.image_type
                    };

                    // 如果是处理后的图片，添加原始图片ID
                    if (imageType === 'processed' && originalHashId) {
                        requestData.original_hash_id = originalHashId;
                    }

                    // 发送请求
                    const saveResponse = await axios({
                        method: 'POST',
                        url: `${API_BASE_URL}/photos/`,
                        headers: {
                            'Authorization': `Bearer ${session?.access_token}`,
                            'Content-Type': 'application/json'
                        },
                        data: requestData
                    });
                    console.log('[API]saveImageToServer response:', saveResponse.data);

                    return {
                        data: saveResponse.data,
                        imageUrl: imgUrl,
                        requestData,
                        existed: true,
                        isDeleted: true
                    };
                } else {
                    // console.log('[API] 图片已存在，跳过上传步骤:', {
                    //     imageType,
                    //     hashId,
                    //     originalHashId,
                    //     imageUrl: queryResult.imageUrl,
                    //     data: queryResult.data
                    // });
                    return {
                        data: queryResult.data,
                        imageUrl: queryResult.imageUrl,
                        requestData: null,
                        existed: true,
                        isDeleted: false
                    };
                }
            }

            if (!imagePublicRuntime.baseURL) {
                throw new Error('invalid_image_public_base_url');
            }

            const uploadDescriptors = buildPhotosUploadDescriptors({
                hashId,
                imageType,
                fileType,
                imageData,
                uploadSource,
                originalHashId,
                originalSource,
                originalFileType
            });

            const uploadResult = await runAssetUploadService.authorizeAndUploadAssets(uploadDescriptors);
            const savedItem = uploadResult[imageType];
            if (!savedItem || !savedItem.resourceKey) {
                throw new Error('invalid_upload_asset');
            }

            const imageUrl = buildImagePublicUrl(savedItem.resourceKey);
            const savedFileType = savedItem.fileType;

            // POST /photos/ 只发送既有关系字段；授权响应的 resource_key 仅用于拼公开 URL，
            // 不得进入 wire contract（删除旧草案的 upload_id/resource_key）。
            const requestData = {
                hash_id: hashId,
                img_url: imageUrl,
                file_type: savedFileType,
                image_type: imageType
            };

            if (imageType === 'processed' && originalHashId) {
                requestData.original_hash_id = originalHashId;
            }

            const saveResponse = await axios({
                method: 'POST',
                url: `${API_BASE_URL}/photos/`,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json'
                },
                data: requestData
            });

            return {
                data: saveResponse.data,
                imageUrl,
                requestData,
                existed: false,
                isDeleted: false
            };
        } catch (error) {
            console.error('[API] save image failed:', error);
            throw error;
        }
    },

    // 测试API请求
    async testApi() {
        const { data: { session } } = await supabase.auth.getSession();
        // console.log('[API] 测试API请求 token:', session?.access_token);

        return axios({
            method: "GET",
            url: `${API_BASE_URL}/get_number/`,
            headers: {
                'Authorization': `Bearer ${session?.access_token}`
            },
        });
    },

    // 获取用户的所有图片列表
    async getUserImages({ params = {}, headers = {} } = {}) {
        const { data: { session } } = await supabase.auth.getSession();

        const requestHeaders = {
            'Authorization': `Bearer ${session?.access_token}`,
            ...headers
        };

        console.log('[0.9.4.9] apiService getUserImages request', {
            url: `${API_BASE_URL}/photos/`,
            params,
            hasFingerprintHeader: !!headers['X-Photoscope-If-Resource-Fingerprint'],
            fingerprintHeaderValue: headers['X-Photoscope-If-Resource-Fingerprint'] || null,
            headerKeys: Object.keys(requestHeaders)
        });

        const response = await axios({
            method: 'GET',
            url: `${API_BASE_URL}/photos/`,
            params,
            headers: requestHeaders
        });

        console.log('[0.9.4.9] apiService getUserImages response', {
            status: response.status,
            hasCacheStatusInBody: !!response.data?.cache_status,
            cacheStatusValue: response.data?.cache_status || null,
            hasFingerprintHeader: !!response.headers?.['x-photoscope-resource-fingerprint'],
            fingerprintHeaderValue: response.headers?.['x-photoscope-resource-fingerprint'] || null,
            hasCacheStatusHeader: !!response.headers?.['x-photoscope-cache-status'],
            cacheStatusHeaderValue: response.headers?.['x-photoscope-cache-status'] || null,
            accessibleHeaderKeys: Object.keys(response.headers || {})
        });

        return {
            data: response.data,
            status: response.status,
            headers: response.headers
        };
    },

    // 加入等待列表
    async joinWaitlist(email) {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const userId = session?.user?.id || null;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
            const language = navigator.language || null;

            // console.log('[API] 加入等待列表:', {
            //     email,
            //     userId,
            //     timeZone,
            //     language
            // })

            const response = await axios({
                method: 'POST',
                url: `${API_BASE_URL}/waitlist/`,
                headers: {
                    'Content-Type': 'application/json'
                },
                data: {
                    email,
                    user_id: userId,
                    timeZone: timeZone,
                    language: language
                }
            });

            if (response.status === 201) {
                return { status: 'success' };
            }
        } catch (error) {
            if (error.response?.status === 409) {
                return { status: 'already_exists' };
            }
            return {
                status: 'error',
                message: error.message
            };
        }
    },

    // 删除图片
    async deleteImage(hashId, userImageRelationId) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await axios({
                method: 'DELETE',
                url: `${API_BASE_URL}/photos/${hashId}/`,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    user_image_relation_id: userImageRelationId
                }
            });

            if (response.status === 204 || response.status === 200) {
                return { success: true };
            }

            return { success: false, message: '删除失败' };
        } catch (error) {
            console.error('[API] 删除图片失败:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message
            };
        }
    },

    // 新增：统一获取图片关系信息（GET /photos/<hash>/）
    async fetchRelation(hashId, options = {}) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const params = {
                image_type: options.imageType || 'original',
                ...(options.imageType === 'processed' && options.originalHashId ? { original_hash_id: options.originalHashId } : {})
            };

            const response = await axios({
                method: 'GET',
                url: `${API_BASE_URL}/photos/${hashId}/`,
                params,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`
                },
                validateStatus: function (status) {
                    return status === 200 || status === 403 || status === 404 || status === 409 || status === 410;
                }
            });

            return {
                status: response.status,
                payload: response.data,
                detail: response.data?.detail || null
            };
        } catch (error) {
            console.error('[API] fetchRelation failed:', error);
            if (error.response) {
                return {
                    status: error.response.status,
                    payload: null,
                    detail: error.response.data?.detail || null
                };
            }
            throw error;
        }
    },

    // 新增：创建或更新图片关系（POST /photos/）
    async createOrUpdateRelation(payload) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await axios({
                method: 'POST',
                url: `${API_BASE_URL}/photos/`,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json'
                },
                data: payload
            });

            return {
                status: response.status,
                payload: response.data,
                detail: null
            };
        } catch (error) {
            console.error('[API] createOrUpdateRelation failed:', error);
            if (error.response) {
                return {
                    status: error.response.status,
                    payload: null,
                    detail: error.response.data?.detail || null
                };
            }
            throw error;
        }
    },

    // 新增：恢复已删除的图片关系（POST /photos/<hash>/restore/）
    async restoreRelation(hashId, relationId) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await axios({
                method: 'POST',
                url: `${API_BASE_URL}/photos/${hashId}/restore/`,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    user_image_relation_id: relationId
                }
            });

            return {
                status: response.status,
                payload: response.data,
                detail: response.data?.detail || null
            };
        } catch (error) {
            console.error('[API] restoreRelation failed:', error);
            if (error.response) {
                return {
                    status: error.response.status,
                    payload: null,
                    detail: error.response.data?.detail || null
                };
            }
            throw error;
        }
    },

    // 新增：刷新图片关系的更新时间（POST /photos/<hash>/touch/）
    async touchRelation(hashId, options = {}) {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const params = {
                ...(options.imageType && { image_type: options.imageType }),
                ...(options.imageType === 'processed' && options.originalHashId ? { original_hash_id: options.originalHashId } : {})
            };

            const response = await axios({
                method: 'POST',
                url: `${API_BASE_URL}/photos/${hashId}/touch/`,
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json'
                },
                params
            });

            return {
                status: response.status,
                payload: response.data,
                detail: response.data?.detail || null
            };
        } catch (error) {
            console.error('[API] touchRelation failed:', error);
            if (error.response) {
                return {
                    status: error.response.status,
                    payload: null,
                    detail: error.response.data?.detail || null
                };
            }
            throw error;
        }
    },

    // 同源代理获取图片 base64 数据（绕过 Safari CORS 限制）
    async fetchImageViaProxy(hashId, options = {}) {
        const {
            imageUrl = null,
            fileType = 'webp'
        } = typeof options === 'string' ? { fileType: options } : (options || {});

        const params = new URLSearchParams({ hashId, fileType });
        if (imageUrl) {
            params.set('imageUrl', imageUrl);
        }

        const response = await axios({
            method: 'GET',
            url: '/api/image-proxy',
            params,
            timeout: 15000
        });

        if (response.data?.success && response.data.data) {
            return {
                success: true,
                data: response.data.data,
                imageType: response.data.imageType,
                fileType: response.data.fileType,
                imageUrl: response.data.imageUrl
            };
        }

        return { success: false, reason: response.data?.reason || 'proxy_failed' };
    }
};

export default apiService;
